import { ComponentType } from '@angular/cdk/portal';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Input,
  signal,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { WidgetYearlyComparisonChangeDetectionObject } from '@api-response-types/forecast-series.response';
import { DataEntry } from '@api-response-types/highcharts-chart-data.response';
import { ForecastVersionStatus } from '@api-types/enums/forecast-version-status.enum';
import { Resolution } from '@api-types/enums/resolution.enum';
import { TOTALS_NAME } from '@api-types/helper/helper';
import { FilterValue } from '@api-types/types/filter-value.type';
import { Filter } from '@api-types/types/filter.type';
import { NgxVirtualSelectFieldOptionModel } from '@components/base-elements/virtual-select-field';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ChartOptionsBuilder } from 'demand-forecasting/components/chart/chart-options-builder';
import { ChartColorService } from 'demand-forecasting/services/chart/chart-color.service';
import { InsightsHttpClient } from 'demand-forecasting/services/http/insights-http-client';
import { selectAvailableFiltersAsRecord } from 'demand-forecasting/store/filter/filter.selectors';
import { latestCompletedForecastSeries } from 'demand-forecasting/store/forecast-series/forecast-series.selectors';
import { selectForecastVersionByQueryParam } from 'demand-forecasting/store/forecast-version/forecast-version.selectors';
import { selectVersionId } from 'demand-forecasting/store/router-state/router.selectors';
import { HighchartsOptions } from 'demand-forecasting/types/forecast-chart/highcharts-options';
import { SeriesOptionsType } from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { LucideScan } from '@lucide/angular';
import {
  FormFieldComponent,
  FormInputSelectComponent,
  H3Component,
  PmButtonComponent,
  PmTooltipDirective,
} from '@libs/ui';

import { DefaultSelectionYearlyComparisonWidget } from '../../types/types';
import {
  WidgetActionsDirective,
  WidgetBaseComponent,
  WidgetContentDirective,
  WidgetHeaderDirective,
  WidgetTitleDirective,
} from '../widget-base/widget-base.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'pm-yearly-comparison-widget',
  templateUrl: './yearly-comparison-widget.component.html',
  imports: [
    TranslateModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    HighchartsChartComponent,
    ReactiveFormsModule,
    FormsModule,
    WidgetBaseComponent,
    WidgetTitleDirective,
    WidgetHeaderDirective,
    WidgetActionsDirective,
    WidgetContentDirective,
    PmTooltipDirective,
    H3Component,
    PmButtonComponent,
    FormFieldComponent,
    FormInputSelectComponent,
  ],
})
export class YearlyComparisonWidgetComponent extends WidgetBaseComponent<YearlyComparisonWidgetComponent> {
  @Input({
    required: false,
  })
  targetUnit?: string;

  @Input({
    required: false,
  })
  filterColumn?: string;

  @Input({
    required: false,
  })
  filterOptions?: FilterValue;

  @Input({
    required: false,
  })
  resolution?: Resolution;
  totalsName = TOTALS_NAME;
  private readonly store = inject(Store);
  private readonly versionId = this.store.selectSignal(selectVersionId);
  private readonly colorService = inject(ChartColorService);
  private readonly insightsHttpClient: InsightsHttpClient =
    inject(InsightsHttpClient);

  // this value is currently always true, but in case we need it its here
  datasetHasValidDataPoints = true;
  totalsFilter: Filter = {
    column: TOTALS_NAME,
    values: [],
  };
  isLoading = true;
  isError = false;

  protected readonly scanIcon = LucideScan;

  selectedValue: FilterValue | null = null;
  possibleFilterValues = signal<FilterValue[]>([]);
  filtersValuesOptions = computed(() => {
    const values = this.possibleFilterValues();
    const options: NgxVirtualSelectFieldOptionModel<FilterValue>[] = values.map(
      (val) => ({
        value: val,
        label: String(val),
        disabled: false,
      }),
    );
    return options;
  });
  protected chartOptions = signal<HighchartsOptions>(
    new ChartOptionsBuilder().build(),
  );

  destroyRef = inject(DestroyRef);

  forecastSeries = this.store.selectSignal(latestCompletedForecastSeries);
  filters = this.store.selectSignal(selectAvailableFiltersAsRecord);
  filtersGroupOptions = computed<NgxVirtualSelectFieldOptionModel<Filter>[]>(
    () =>
      this.filters().map((f) => ({
        value: f,
        label: f.column,
        disabled: false,
      })),
  );

  private readonly forecastVersion = this.store.selectSignal(
    selectForecastVersionByQueryParam,
  );

  protected selectedFilterFormControl = new FormControl<Filter>(
    {
      value: this.filters()[0],
      disabled: false,
    },
    {
      nonNullable: true,
    },
  );

  protected selectedValueFormControl = new FormControl<FilterValue>(
    {
      value: '',
      disabled: false,
    },
    { nonNullable: true },
  );

  previousYearlyComparisonChangeDetectionObject: WidgetYearlyComparisonChangeDetectionObject;

  public data: DefaultSelectionYearlyComparisonWidget = inject(
    MAT_DIALOG_DATA,
    { optional: true },
  );

  constructor() {
    super();

    if (this.data) {
      this.targetUnit = this.data.targetUnit;
      this.resolution = this.data.resolution;
      this.filterColumn = this.data.filterColumn;
      this.filterOptions = this.data.filterOptions;
    }

    effect(() => {
      if (!this.resolution) {
        this.resolution = Resolution.Monthly;
      }
      const forecastSeries = this.forecastSeries();
      const forecastVersion = this.forecastVersion();
      const filters = this.filters();
      if (filters && !this.selectedFilterFormControl.getRawValue()) {
        this.selectedFilterFormControl.setValue(filters[0]);
      }

      if (
        !forecastSeries ||
        forecastVersion?.status !== ForecastVersionStatus.FINISHED ||
        !this.selectedFilterFormControl.getRawValue()
      ) {
        console.warn(
          '[WIDGET_YEARLY_COMPARISON] No forecast series or forecast version or selected filters found',
        );
        return;
      }

      const newSeriesChangeDetectionObject: WidgetYearlyComparisonChangeDetectionObject =
        {
          _resultSetStatus: forecastSeries._resultSetStatus,
          configurationId: forecastSeries.configurationId,
          resultSetId: forecastVersion.id, // TODO forecastSeries.resultSetId seems to never get updated
          yAxis: forecastSeries.yAxis,
          resolution: this.resolution,
        };

      if (
        JSON.stringify(newSeriesChangeDetectionObject) ===
        JSON.stringify(this.previousYearlyComparisonChangeDetectionObject ?? {})
      ) {
        console.warn('[WIDGET_YEARLY_COMPARISON] No changes');
        return;
      }

      this.previousYearlyComparisonChangeDetectionObject =
        newSeriesChangeDetectionObject;
      this.setDefaultFilter();
      this.loadYearlyComparisonData();
    });

    this.selectedFilterFormControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((changedValue) => {
        if (changedValue) this.onFilterChange(changedValue);
      });
    this.selectedValueFormControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.onFilterValueChange();
      });
  }

  getDefaultSelectionForFilterValue() {
    return this.selectedFilterFormControl.getRawValue()?.values[0];
  }

  buildChartOptions(data: {
    [key: string]: {
      actuals: DataEntry;
      forecast: DataEntry;
    };
  }) {
    return new ChartOptionsBuilder()
      .withPlotOptions({
        series: {
          allowPointSelect: false,
          marker: {
            enabled: true,
          },
        },
      })
      .withXAxis({
        type: 'datetime',
        title: {
          text: this.forecastSeries()?.xAxis || 'date',
        },
        dateTimeLabelFormats: {
          month: '%b ',
        },
      })
      .withYAxis({
        title: {
          text: this.targetUnit || this.forecastSeries()?.yAxis || 'value',
        },
        min: 0,
      })
      .withTooltip({
        dateTimeLabelFormats: {
          month: '%B',
          day: '%B',
          week: '%B',
          year: '%B',
          hour: '%B',
        },
      })
      .withSeries(
        Object.values(data).flatMap((value, idx): SeriesOptionsType[] => {
          const values: SeriesOptionsType[] = [];
          const color = this.colorService.getColor(idx);
          const actualsSeries: SeriesOptionsType = {
            type: 'line',
            name: `Actuals ${Object.keys(data)[idx]}`,
            data: value.actuals,
            color,
          };
          const forecastSeries: SeriesOptionsType = {
            type: 'line',
            dashStyle: 'Dash',
            name: `Forecast ${Object.keys(data)[idx]}`,
            data: value.forecast,
            color,
          };
          if (value.actuals.find((entry) => entry[1] !== null)) {
            values.push(actualsSeries);
          }
          if (value.forecast.find((entry) => entry[1] !== null)) {
            values.push(forecastSeries);
          }
          return values;
        }),
      )
      .build();
  }
  loadYearlyComparisonData(): void {
    const formControlValue = this.selectedFilterFormControl.getRawValue();
    if (formControlValue && this.forecastSeries() !== undefined) {
      this.isLoading = true;
      this.isError = false;
      const isTotals = formControlValue.column === TOTALS_NAME;
      const filterValue = isTotals
        ? undefined
        : this.selectedValueFormControl.getRawValue();
      const filterName = isTotals ? undefined : formControlValue.column;
      const versionId = this.versionId();
      if (!versionId) {
        console.error('[Yearly Comparison] No versionId found');
        return;
      }
      this.insightsHttpClient
        .getYearlyComparisonData(
          versionId,
          filterValue,
          filterName,
          this.resolution,
          this.targetUnit,
        )
        .subscribe(
          (res) => {
            const options = this.buildChartOptions(res);
            this.chartOptions.set(options);
            this.isLoading = false;
          },
          () => {
            this.isError = true;
            this.isLoading = false;
          },
        );
    }
  }

  onFilterChange(newValue: Filter): void {
    this.isLoading = true;

    if (newValue.column === TOTALS_NAME) {
      this.possibleFilterValues.set(newValue.values);
      this.selectedValueFormControl.disable();
    }

    if (newValue.column !== TOTALS_NAME) {
      this.selectedValueFormControl.enable({ emitEvent: false });
      this.possibleFilterValues.set(
        this.selectedFilterFormControl.value.values || [],
      );
      this.selectedValue = this.getDefaultSelectionForFilterValue();
      this.selectedValueFormControl.setValue(
        this.getDefaultSelectionForFilterValue(),
      );
    }
  }

  setDefaultFilter() {
    if (this.filterColumn === TOTALS_NAME) {
      this.possibleFilterValues.set([]);
      this.selectedValueFormControl.disable();
      this.selectedFilterFormControl.setValue(this.totalsFilter);
      return;
    }

    // first select
    if (this.filterColumn) {
      const defaultFilter =
        this.filters().find((filter) => filter.column === this.filterColumn) ??
        this.filters()[0];
      this.selectedFilterFormControl.setValue(defaultFilter);
    } else {
      this.selectedFilterFormControl.setValue(this.filters()[0]);
    }

    // second select
    if (this.filterOptions) {
      this.selectedValue = this.filterOptions;
      this.selectedValueFormControl.setValue(this.filterOptions);
    } else {
      this.selectedValue = this.getDefaultSelectionForFilterValue();
      this.selectedValueFormControl.setValue(
        this.getDefaultSelectionForFilterValue(),
      );
    }
    this.possibleFilterValues.set(
      this.selectedFilterFormControl.getRawValue()?.values || [],
    );
  }

  onFilterValueChange(): void {
    this.loadYearlyComparisonData();
  }

  // Override the showFullScreen method from the WidgetBaseComponent to handle afterClosed event properly
  showFullScreen(
    componentType: ComponentType<YearlyComparisonWidgetComponent>,
    filterSelection?: DefaultSelectionYearlyComparisonWidget,
  ): MatDialogRef<YearlyComparisonWidgetComponent> | null {
    const dialog = super.showFullScreen(componentType, filterSelection);
    dialog?.afterClosed().subscribe(() => {
      this.filterColumn =
        dialog.componentInstance.selectedFilterFormControl.value.column;
      this.filterOptions =
        dialog.componentInstance.selectedValueFormControl.value;
      this.setDefaultFilter();
      this.loadYearlyComparisonData();
    });
    return null;
  }

  get YearlyComparisonWidgetComponent(): typeof YearlyComparisonWidgetComponent {
    return YearlyComparisonWidgetComponent;
  }
}
